import { Autocomplete, MenuItem, TextField } from '@mui/material';
import { ISftpPartner } from '@core/models/sftp-transfer.model';
import { Supplier } from '@core/models/supplier.model';
import { IUser } from '@core/models/user-listing.model';
import Modal from 'Common/Components/Modal';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import { useFormik } from 'formik';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { getSuppliersThunk } from '@redux/features/supplier.slice';
import { getAllUsersThunk } from '@redux/features/user-listing.slice';
import { RootState } from '@redux/store';
import * as Yup from 'yup';
import sftpIntegrationService from 'services/api/sftp-integration.service';
import { isEnvCredentialPartner } from './sftp-partner.utils';
import { wizardErrorMessage } from './sftp-user-messages';

interface IProps {
    show: boolean;
    handleClose: () => void;
    onSubmit: (values: Record<string, unknown>) => Promise<void>;
    initial?: ISftpPartner | null;
    isEdit?: boolean;
}

type Direction = 'RECEIVE' | 'SEND' | 'BIDIRECTIONAL';
type AuthType = 'PASSWORD' | 'KEY' | 'PASSWORD_AND_KEY';

const AUTH_TYPE_OPTIONS: { value: AuthType; label: string }[] = [
    { value: 'PASSWORD', label: 'PASSWORD' },
    { value: 'KEY', label: 'KEY' },
    { value: 'PASSWORD_AND_KEY', label: 'PASSWORD_AND_KEY' },
];

const defaultValues = {
    code: '',
    name: '',
    direction: 'RECEIVE' as Direction,
    host: '',
    port: 5022,
    username: '',
    authType: 'PASSWORD' as AuthType,
    passwordEnvKey: '',
    privateKeyPathEnvKey: '',
    knownHostsPathEnvKey: '',
    remotePollPath: '',
    remoteUploadPath: '',
    remoteArchivePath: '',
    remoteQuarantinePath: '',
    filePatterns: '["*.xml","*.json"]',
    enabled: true,
    pollCron: '0 */5 * * * *',
    partnerGln: '',
    partnerDuns: '',
    localStorageSubdir: '',
    epcisSchemaVersion: '1.2',
    epcisFormat: 'XML',
    requiresAdminApproval: true,
    autoSendOnShip: false,
    environment: 'PRODUCTION',
    deliveryMode: 'REMOTE' as 'REMOTE' | 'HOSTED',
    antarSftpAccountUsername: '',
    supplierId: null as number | null,
    pharmacyUserId: null as number | null,
};

function getUsersPageContent(raw: unknown): IUser[] {
    const u = raw as Record<string, unknown> | null | undefined;
    if (!u) return [];
    const inner = (u.usersWithPage as Record<string, unknown>) || u;
    return (inner.content as IUser[]) || [];
}

const SftpPartnerModal: FC<IProps> = ({ show, handleClose, onSubmit, initial, isEdit }) => {
    const dispatch = useAppDispatch();
    const [activeTab, setActiveTab] = useState<'settings' | 'credentials'>('settings');
    const [credPassword, setCredPassword] = useState('');
    const [credPem, setCredPem] = useState('');
    const [credSaving, setCredSaving] = useState(false);
    const [credMessage, setCredMessage] = useState<string | null>(null);
    const { suppliers } = useAppSelector((state: RootState) => (state as RootState).supplierState || { suppliers: [] });
    const { users: usersRaw } = useAppSelector((state: RootState) => state.userListingState);

    const pharmacyUsers = useMemo(() => {
        return getUsersPageContent(usersRaw).filter((u) => u.account?.accountType === 'PHARMACY');
    }, [usersRaw]);

    const validationSchema = Yup.object({
        code: isEdit ? Yup.string() : Yup.string().required('Code is required'),
        name: Yup.string().required('Name is required'),
        direction: Yup.string().required('Direction is required'),
        host: Yup.string().required('Host is required'),
        port: Yup.number().min(1).max(65535).required('Port is required'),
        username: Yup.string().required('Username is required'),
        remotePollPath: Yup.string().when('direction', {
            is: (d: string) => d === 'RECEIVE' || d === 'BIDIRECTIONAL',
            then: (s) => s.required('Poll path is required for receive'),
            otherwise: (s) => s,
        }),
        remoteUploadPath: Yup.string().when('direction', {
            is: (d: string) => d === 'SEND' || d === 'BIDIRECTIONAL',
            then: (s) => s.required('Upload path is required for send'),
            otherwise: (s) => s,
        }),
        authType: Yup.string().required('Auth type is required'),
        passwordEnvKey: Yup.string().when(['authType', '$envPartner'], {
            is: (a: string, envPartner: boolean) => envPartner && (a === 'PASSWORD' || a === 'PASSWORD_AND_KEY'),
            then: (s) => s.required('Password env key is required'),
            otherwise: (s) => s.nullable(),
        }),
        privateKeyPathEnvKey: Yup.string().when(['authType', '$envPartner'], {
            is: (a: string, envPartner: boolean) => envPartner && (a === 'KEY' || a === 'PASSWORD_AND_KEY'),
            then: (s) => s.required('Private key path env key is required'),
            otherwise: (s) => s.nullable(),
        }),
        supplierId: Yup.number().nullable().when('direction', {
            is: (d: string) => d === 'RECEIVE' || d === 'BIDIRECTIONAL',
            then: (s) => s.required('Supplier is required'),
            otherwise: (s) => s.nullable(),
        }),
        pharmacyUserId: Yup.number().nullable().when('direction', {
            is: (d: string) => d === 'SEND' || d === 'BIDIRECTIONAL',
            then: (s) => s.required('Pharmacy is required'),
            otherwise: (s) => s.nullable(),
        }),
    });

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initial
            ? {
                  ...defaultValues,
                  ...initial,
                  direction: (initial.direction || 'RECEIVE') as Direction,
                  authType: (initial.authType || 'PASSWORD_AND_KEY') as AuthType,
                  port: initial.port ?? 5022,
                  enabled: initial.enabled ?? true,
                  requiresAdminApproval: initial.requiresAdminApproval ?? true,
                  supplierId: initial.supplierId ?? null,
                  pharmacyUserId: initial.pharmacyUserId ?? null,
              }
            : defaultValues,
        validationSchema,
        context: { envPartner: isEnvCredentialPartner(initial || {}) },
        onSubmit: async (values) => {
            const direction = values.direction as Direction;
            const payload: Record<string, unknown> = { ...values };
            const auth = values.authType as AuthType;
            if (direction === 'RECEIVE') {
                payload.pharmacyUserId = null;
            } else if (direction === 'SEND') {
                payload.supplierId = null;
            }
            if (auth === 'PASSWORD') {
                payload.privateKeyPathEnvKey = '';
                payload.knownHostsPathEnvKey = '';
            } else if (auth === 'KEY') {
                payload.passwordEnvKey = '';
            }
            await onSubmit(payload);
            formik.resetForm();
            handleClose();
        },
    });

    const envPartner = isEnvCredentialPartner(initial || {});

    const saveCredentials = async () => {
        if (!initial?.code) return;
        setCredSaving(true);
        setCredMessage(null);
        try {
            await sftpIntegrationService.saveCredentials(initial.code, {
                password: credPassword || undefined,
                privateKeyPem: credPem || undefined,
            });
            setCredMessage('Credentials saved.');
            setCredPassword('');
            setCredPem('');
        } catch (err) {
            setCredMessage(wizardErrorMessage(err, 'Could not save credentials.'));
        } finally {
            setCredSaving(false);
        }
    };

    const discoverHostKey = async () => {
        if (!initial?.code) return;
        try {
            const res = await sftpIntegrationService.discoverHostKey(initial.code);
            setCredMessage(res.data.hostKeyDiscovery?.fingerprint || 'Host key discovered.');
        } catch (err) {
            setCredMessage(wizardErrorMessage(err, 'Could not discover host key.'));
        }
    };
    const direction = formik.values.direction as Direction;
    const authType = formik.values.authType as AuthType;
    const showSupplier = direction === 'RECEIVE' || direction === 'BIDIRECTIONAL';
    const showPharmacy = direction === 'SEND' || direction === 'BIDIRECTIONAL';
    const showPasswordEnv = envPartner && (authType === 'PASSWORD' || authType === 'PASSWORD_AND_KEY');
    const showKeyEnv = envPartner && (authType === 'KEY' || authType === 'PASSWORD_AND_KEY');
    const showOutboundDelivery = showPharmacy;
    const deliveryMode = (formik.values.deliveryMode || 'REMOTE') as 'REMOTE' | 'HOSTED';

    useEffect(() => {
        if (show) {
            dispatch(getSuppliersThunk());
            dispatch(
                getAllUsersThunk({
                    pageNumber: '0',
                    pageSize: '500',
                    accountType: 'PHARMACY',
                })
            );
        } else {
            formik.resetForm();
        }
    }, [show, dispatch]);

    useEffect(() => {
        if (direction === 'RECEIVE') {
            formik.setFieldValue('pharmacyUserId', null);
        } else if (direction === 'SEND') {
            formik.setFieldValue('supplierId', null);
        }
    }, [direction]);

    useEffect(() => {
        if (authType === 'PASSWORD') {
            formik.setFieldValue('privateKeyPathEnvKey', '');
            formik.setFieldValue('knownHostsPathEnvKey', '');
        } else if (authType === 'KEY') {
            formik.setFieldValue('passwordEnvKey', '');
        }
    }, [authType]);

    const selectedSupplier =
        (suppliers as Supplier[]).find((s) => s.id === formik.values.supplierId) || null;
    const selectedPharmacy =
        pharmacyUsers.find((u) => u.id === formik.values.pharmacyUserId) || null;

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="mx-auto w-full max-w-[calc(100vw-1rem)] sm:max-w-[640px] p-4 shadow"
        >
            <Modal.Header className="shrink-0 border-b border-slate-200 pb-3">
                <Modal.Title className="text-lg font-semibold text-slate-800">
                    {isEdit ? `Edit partner ${initial?.code}` : 'Add SFTP partner'}
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="py-4">
                {isEdit && (
                    <div className="mb-4 flex gap-2 border-b border-slate-200 pb-2">
                        <button type="button" className={`px-3 py-1 text-sm ${activeTab === 'settings' ? 'font-semibold text-[#bd902e]' : 'text-slate-600'}`} onClick={() => setActiveTab('settings')}>Settings</button>
                        <button type="button" className={`px-3 py-1 text-sm ${activeTab === 'credentials' ? 'font-semibold text-[#bd902e]' : 'text-slate-600'}`} onClick={() => setActiveTab('credentials')}>Credentials</button>
                    </div>
                )}
                {activeTab === 'credentials' && isEdit ? (
                    <div className="flex flex-col gap-3">
                        {envPartner ? (
                            <p className="rounded bg-green-50 p-3 text-sm text-green-900">
                                This integration uses credentials managed on the server. To update, contact your system administrator.
                                {initial?.credentialsConfigured ? ' Credentials are configured.' : ' Credentials may be missing on the server.'}
                            </p>
                        ) : (
                            <>
                                <p className="text-sm text-slate-600">Enter new password or upload a private key. Leave blank to keep unchanged.</p>
                                <TextField size="small" type="password" label="SFTP password" value={credPassword} onChange={(e) => setCredPassword(e.target.value)} />
                                <div>
                                    <label className="mb-1 block text-sm font-medium text-slate-700">Private key (.pem)</label>
                                    <input type="file" accept=".pem,.key,.txt" onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (!f) return;
                                        const reader = new FileReader();
                                        reader.onload = () => setCredPem(String(reader.result || ''));
                                        reader.readAsText(f);
                                    }} />
                                </div>
                                {initial?.hostKeyFingerprint && (
                                    <p className="text-xs text-slate-600">Host key: {initial.hostKeyFingerprint}</p>
                                )}
                                {credMessage && <p className="text-sm text-slate-700">{credMessage}</p>}
                                <div className="flex flex-wrap gap-2">
                                    <ButtonBase innerText={credSaving ? 'Saving…' : 'Save credentials'} onClick={saveCredentials} disabled={credSaving} />
                                    <ButtonBase innerText="Discover host key" onClick={discoverHostKey} className="!bg-slate-200 !text-slate-800" />
                                </div>
                            </>
                        )}
                    </div>
                ) : (
                <>
                <p className="mb-4 text-sm text-slate-600">
                    {envPartner
                        ? 'Update connection settings. Credentials for this partner are managed on the server.'
                        : 'Update partner connection and paths.'}
                </p>
                <form onSubmit={formik.handleSubmit} className="flex flex-col gap-5">
                    <section>
                        <h4 className="mb-2 text-sm font-semibold uppercase text-[#bd902e]">Partner link</h4>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <TextField
                                select
                                size="small"
                                fullWidth
                                name="direction"
                                label="Direction *"
                                value={formik.values.direction}
                                onChange={(e) => formik.setFieldValue('direction', e.target.value)}
                                onBlur={formik.handleBlur}
                            >
                                <MenuItem value="RECEIVE">RECEIVE (from supplier)</MenuItem>
                                <MenuItem value="SEND">SEND (to pharmacy)</MenuItem>
                                <MenuItem value="BIDIRECTIONAL">BIDIRECTIONAL</MenuItem>
                            </TextField>
                            <div className="flex items-center text-xs text-slate-500 md:col-span-1">
                                Receive links a supplier; Send links a pharmacy account.
                            </div>
                        </div>
                        <div className="mt-3 grid grid-cols-1 gap-3">
                            {showSupplier && (
                                <Autocomplete
                                    options={suppliers || []}
                                    getOptionLabel={(option) =>
                                        option.businessName || option.email || `Supplier #${option.id}`
                                    }
                                    value={selectedSupplier}
                                    onChange={(_, newValue) => {
                                        formik.setFieldValue('supplierId', newValue?.id ?? null);
                                        if (newValue?.supplierGln) {
                                            formik.setFieldValue('partnerGln', newValue.supplierGln);
                                        }
                                        if (newValue?.dunsNumber) {
                                            formik.setFieldValue('partnerDuns', newValue.dunsNumber);
                                        }
                                    }}
                                    onBlur={() => formik.setFieldTouched('supplierId', true)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Supplier *"
                                            size="small"
                                            error={formik.touched.supplierId && Boolean(formik.errors.supplierId)}
                                            helperText={
                                                (formik.touched.supplierId && formik.errors.supplierId) as string
                                            }
                                        />
                                    )}
                                />
                            )}
                            {showPharmacy && (
                                <Autocomplete
                                    options={pharmacyUsers}
                                    getOptionLabel={(option) => {
                                        const name = option.account?.businessName;
                                        return name ? `${name} (${option.email})` : option.email;
                                    }}
                                    value={selectedPharmacy}
                                    onChange={(_, newValue) => {
                                        formik.setFieldValue('pharmacyUserId', newValue?.id ?? null);
                                        if (newValue?.account?.pharmacyGln) {
                                            formik.setFieldValue('partnerGln', newValue.account.pharmacyGln);
                                        }
                                    }}
                                    onBlur={() => formik.setFieldTouched('pharmacyUserId', true)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Pharmacy *"
                                            size="small"
                                            error={
                                                formik.touched.pharmacyUserId && Boolean(formik.errors.pharmacyUserId)
                                            }
                                            helperText={
                                                (formik.touched.pharmacyUserId &&
                                                    formik.errors.pharmacyUserId) as string
                                            }
                                        />
                                    )}
                                />
                            )}
                        </div>
                    </section>

                    <section>
                        <h4 className="mb-2 text-sm font-semibold uppercase text-[#bd902e]">Connection</h4>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            {!isEdit && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="code"
                                    label="Code (e.g. ACME_PHARM)"
                                    {...fieldProps(formik, 'code')}
                                />
                            )}
                            <TextField size="small" fullWidth name="name" label="Name *" {...fieldProps(formik, 'name')} />
                            <TextField size="small" fullWidth name="host" label="Host *" {...fieldProps(formik, 'host')} />
                            <TextField
                                size="small"
                                fullWidth
                                name="port"
                                label="Port *"
                                type="number"
                                {...fieldProps(formik, 'port')}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                name="username"
                                label="Username *"
                                {...fieldProps(formik, 'username')}
                            />
                            {!envPartner && (
                                <TextField
                                    select
                                    size="small"
                                    fullWidth
                                    name="authType"
                                    label="Auth type *"
                                    value={formik.values.authType}
                                    onChange={(e) => formik.setFieldValue('authType', e.target.value)}
                                    onBlur={formik.handleBlur}
                                >
                                    {AUTH_TYPE_OPTIONS.map((opt) => (
                                        <MenuItem key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            )}
                            {(direction === 'RECEIVE' || direction === 'BIDIRECTIONAL') && (
                            <TextField
                                size="small"
                                fullWidth
                                name="remotePollPath"
                                label="Remote poll path"
                                {...fieldProps(formik, 'remotePollPath')}
                            />
                            )}
                            {(direction === 'SEND' || direction === 'BIDIRECTIONAL') && (
                            <TextField
                                size="small"
                                fullWidth
                                name="remoteUploadPath"
                                label="Remote upload path"
                                {...fieldProps(formik, 'remoteUploadPath')}
                            />
                            )}
                            <TextField
                                size="small"
                                fullWidth
                                name="remoteArchivePath"
                                label="Archive path"
                                {...fieldProps(formik, 'remoteArchivePath')}
                            />
                        </div>
                    </section>

                    {showOutboundDelivery && (
                    <section>
                        <h4 className="mb-2 text-sm font-semibold uppercase text-[#bd902e]">Outbound delivery</h4>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <TextField
                                select
                                size="small"
                                fullWidth
                                name="deliveryMode"
                                label="Delivery mode"
                                value={formik.values.deliveryMode}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="REMOTE">REMOTE — upload to partner SFTP</MenuItem>
                                <MenuItem value="HOSTED">HOSTED — partner picks up from Antar</MenuItem>
                            </TextField>
                            {deliveryMode === 'HOSTED' && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="antarSftpAccountUsername"
                                    label="Antar SFTP account username"
                                    placeholder="e.g. pharmacy_test"
                                    {...fieldProps(formik, 'antarSftpAccountUsername')}
                                />
                            )}
                            <TextField
                                select
                                size="small"
                                fullWidth
                                name="environment"
                                label="Environment"
                                value={formik.values.environment}
                                onChange={formik.handleChange}
                            >
                                <MenuItem value="PRODUCTION">PRODUCTION</MenuItem>
                                <MenuItem value="TEST">TEST</MenuItem>
                            </TextField>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    className="size-4 rounded border-slate-300"
                                    name="autoSendOnShip"
                                    checked={!!formik.values.autoSendOnShip}
                                    onChange={formik.handleChange}
                                />
                                Auto-send EPCIS on ship
                            </label>
                        </div>
                    </section>
                    )}

                    {envPartner && (
                    <section>
                        <h4 className="mb-2 text-sm font-semibold uppercase text-[#bd902e]">Server credentials (read-only)</h4>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <TextField
                                select
                                size="small"
                                fullWidth
                                name="authType"
                                label="Auth type *"
                                value={formik.values.authType}
                                onChange={(e) => formik.setFieldValue('authType', e.target.value)}
                                onBlur={formik.handleBlur}
                            >
                                {AUTH_TYPE_OPTIONS.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {showPasswordEnv && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="passwordEnvKey"
                                    label="Password env key *"
                                    placeholder="e.g. LOCAL_SFTP_PASSWORD"
                                    {...fieldProps(formik, 'passwordEnvKey')}
                                />
                            )}
                            {showKeyEnv && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="privateKeyPathEnvKey"
                                    label="Private key path env key *"
                                    placeholder="e.g. ACME_SFTP_PRIVATE_KEY_PATH"
                                    {...fieldProps(formik, 'privateKeyPathEnvKey')}
                                />
                            )}
                            {showKeyEnv && (
                                <TextField
                                    size="small"
                                    fullWidth
                                    name="knownHostsPathEnvKey"
                                    label="Known hosts env key (optional)"
                                    {...fieldProps(formik, 'knownHostsPathEnvKey')}
                                />
                            )}
                        </div>
                    </section>
                    )}

                    <section>
                        <h4 className="mb-2 text-sm font-semibold uppercase text-[#bd902e]">Traceability & settings</h4>
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <TextField
                                size="small"
                                fullWidth
                                name="partnerGln"
                                label="Partner GLN"
                                {...fieldProps(formik, 'partnerGln')}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                name="partnerDuns"
                                label="Partner DUNS"
                                {...fieldProps(formik, 'partnerDuns')}
                            />
                            <TextField
                                size="small"
                                fullWidth
                                name="pollCron"
                                label="Poll cron"
                                {...fieldProps(formik, 'pollCron')}
                            />
                        </div>
                        <div className="mt-3 flex flex-wrap gap-4">
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    className="size-4 rounded border-slate-300"
                                    name="enabled"
                                    checked={formik.values.enabled}
                                    onChange={formik.handleChange}
                                />
                                Enabled
                            </label>
                            <label className="flex items-center gap-2 text-sm text-slate-700">
                                <input
                                    type="checkbox"
                                    className="size-4 rounded border-slate-300"
                                    name="requiresAdminApproval"
                                    checked={formik.values.requiresAdminApproval}
                                    onChange={formik.handleChange}
                                />
                                Requires admin approval
                            </label>
                        </div>
                    </section>
                </form>
                </>
                )}
            </Modal.Body>
            <Modal.Footer className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 pt-3">
                <ButtonBase innerText="Cancel" onClick={handleClose} className="!bg-slate-200 !border-slate-300 !text-slate-800 hover:!bg-slate-300" />
                {activeTab === 'settings' && (
                    <ButtonBase innerText="Save" onClick={() => formik.handleSubmit()} />
                )}
            </Modal.Footer>
        </Modal>
    );
};

function fieldProps(formik: ReturnType<typeof useFormik>, name: string) {
    const values = formik.values as Record<string, unknown>;
    const touched = formik.touched as Record<string, boolean>;
    const errors = formik.errors as Record<string, string>;
    return {
        value: values[name] as string | number,
        onChange: formik.handleChange,
        onBlur: formik.handleBlur,
        error: touched[name] && Boolean(errors[name]),
        helperText: touched[name] && errors[name],
    };
}

export default SftpPartnerModal;
